import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TextField, SelectField } from "../../NextComponents/Fields";
import countries from "../../../utils/countries";
import { createSupportConversation } from "../../../api/customerSupport";
import BusinessAutocomplete from "../BusinessAutocomplete/BusinessAutocomplete";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import onelykLogo from "../../../Assets/onelyklogo.svg";

const CustomerEntry = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [placeId, setPlaceId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+1",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBusinessSelected = (business) => {
    if (!business) {
      setPlaceId(null);
      return;
    }

    setPlaceId(business.placeId);
    setErrors((prev) => ({ ...prev, business: "" }));

    setFormData((prev) => ({
      ...prev,
      name: prev.name || business.businessName || "",
      email: prev.email || business.email || "",
      countryCode: business.countryCode || prev.countryCode,
      phoneNumber: business.phoneNumber || prev.phoneNumber,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!placeId) {
      newErrors.business = "Please select your business from Google";
    }
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Valid email is required";
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await createSupportConversation({
        ...formData,
        placeId,
      });
      if (result.isExisting) {
        toast.info("Welcome back! Continuing your existing chat.");
      } else {
        toast.success("Check your email for your chat link!");
      }
      navigate(`/customer/${result.shortCode}`, {
        replace: true,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to start support chat",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4 sm:p-6 pt-safe pb-safe">
      {loading && <LoadingSpinner />}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5 sm:p-8">
        <div className="flex justify-center mb-6">
          <img src={onelykLogo} alt="Onelyk" className="h-10" />
        </div>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 text-center mb-2">
          Chat With Us
        </h1>
        <p className="text-gray-500 text-center text-sm mb-6 sm:mb-8">
          Find your business on Google and we&apos;ll connect you with our support
          team.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <BusinessAutocomplete
            onBusinessSelected={handleBusinessSelected}
            error={errors.business}
          />

          <div>
            <TextField
              label="Your name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-[110px_1fr] gap-3">
            <div>
              <SelectField
                label="Code"
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </SelectField>
            </div>
            <div>
              <TextField
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Phone number"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 rounded-lg bg-indigo-600 px-4 py-3.5 sm:py-3 text-base sm:text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 active:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 touch-manipulation"
          >
            Start Chat
          </button>
        </form>
      </div>
    </div>
  );
};

export default CustomerEntry;
